import { Injectable, NgZone, OnDestroy, Renderer2 } from '@angular/core';

import { TransformStore } from '@mhe/reader/state/transform';
import * as transformActions from '@mhe/reader/state/transform/transform.actions';
import { Book, SpineItem } from '@mhe/reader/models';
import { Next, Transformer } from './transformer';
import { DevicePlatform, DeviceService } from '@mhe/reader/common';

@Injectable()
export class SelectionTransformer implements Transformer, OnDestroy {
  private unlistenerRegistry: Record<string, () => void> = {};

  private activeSelection: Selection | undefined;

  constructor(
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    private readonly transformStore: TransformStore,
    private readonly deviceService: DeviceService,
  ) {}

  async postRender(
    book: Book,
    spineItem: SpineItem,
    content: HTMLDocument,
    iframe: HTMLIFrameElement,
    next: Next,
  ): Promise<HTMLDocument> {
    const devicePlatform = this.deviceService.platformType;

    // do any cleanup from previous renders first
    this.unlistenerRegistry[iframe.id]?.();

    const selectionEndFn = (e): void => {
      this.zone.run(() => {
        const selection = content.getSelection();
        const details: {
          nonTextFlag?: boolean
          type: 'math' | 'media' | 'widget' | 'image'
        } = e?.detail || null;

        if (selection?.type?.toLowerCase() !== 'range') {
          this.activeSelection?.removeAllRanges();
          this.activeSelection = undefined;
          this.transformStore.dispatch(transformActions.rangeRemoved());
        }

        if (selection?.type?.toLowerCase() === 'range') {
          if (this.activeSelection && this.activeSelection !== selection) {
            this.activeSelection?.removeAllRanges();
          }

          this.activeSelection = selection;
          this.transformStore.dispatch(
            transformActions.rangeSelected({
              selection,
              devicePlatform,
              details,
            }),
          );
        }
      });
    };
    // When using Caret Browsing, text is selected via keyboard (Shift+Arrow keys) so
    // mouseup never fires. Pressing Enter/Space while a range is selected opens the context menu
    // immediately (using Desktop platform so the overlay doesn't wait for a mobile button).
    const enterKeyFn = (e: KeyboardEvent): void => {
      const isActivationKey = e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar';
      if (!isActivationKey) return;
      const selection = content.getSelection();
      if (selection?.type?.toLowerCase() === 'range') {
        e.preventDefault();
        this.zone.run(() => {
          this.transformStore.dispatch(
            transformActions.rangeSelected({
              selection,
              devicePlatform: DevicePlatform.Desktop,
            }),
          );
        });
      }
    };

    // Registered on all platforms because touch laptops may be detected as non-Desktop.
    const unlistenEnter = this.renderer.listen(content, 'keydown', enterKeyFn);

    if (devicePlatform !== DevicePlatform.Desktop) {
      const unlistenSelectionChange = this.renderer.listen(content, 'selectionchange', selectionEndFn);
      this.unlistenerRegistry[iframe.id] = () => {
        unlistenSelectionChange();
        unlistenEnter();
      };
    } else {
      const unlistenMouseup = this.renderer.listen(content, 'mouseup', selectionEndFn);
      this.unlistenerRegistry[iframe.id] = () => {
        unlistenMouseup();
        unlistenEnter();
      };
    }

    return await next(content);
  }

  ngOnDestroy(): void {
    Object.values(this.unlistenerRegistry).forEach((unlistener) =>
      unlistener(),
    );
  }
}
